"use client";

import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faHome,
  faClipboardList,
  faSignOutAlt,
  faChevronRight,
  faBars,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import d1 from "../logo/logo1png2.png";
import Cookies from "js-cookie";
import { useDispatch, useSelector } from "react-redux";
import "../NavbarCSS/Dummy.css";
import { logoutAsync } from "../../../../features/auth/authSlices";
import RoleBasedProtectedRoute from "../../../../features/common/RoleBasedProtectedRoute";
import { Button, Image } from "@chakra-ui/react";
import {
  fetchProfileAsync,
  selectCompanyProfile,
} from "../../../../features/employerDashboard/employerDashAPIs/companyProfile/CompanyProfileSlice";
import { EMPLOYER_COMPANY_LOGO_SAVE_URL } from "../../../../features/common/URLs";
import { Bars3BottomLeftIcon } from "@heroicons/react/24/outline";

export function DummyAfterLogin() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // mobile sidebar toggle state
  const [isNotificationOpen, setIsNotificationOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const notificationRef = useRef(null);
  const dropdownRef = useRef(null);
  const userButtonRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const companyProfile = useSelector(selectCompanyProfile);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const userData = {
    name: companyProfile?.company_name || "Guest",
    email: companyProfile?.email || "guest@example.com",
  };

  const toggleDrawer = () => setIsDrawerOpen((p) => !p);

  const toggleSidebar = () => {
    const next = !isSidebarOpen;
    setIsSidebarOpen(next);
    // Notify Menu_Lg_Bar and Edashboard
    const event = new CustomEvent("toggleSidebar", { detail: { isOpen: next } });
    document.dispatchEvent(event);
  };

  // Keep local toggle icon state in sync if other components close it
  useEffect(() => {
    const handleExternalToggle = (e) => {
      setIsSidebarOpen(e.detail.isOpen);
    };
    document.addEventListener("toggleSidebar", handleExternalToggle);
    return () => document.removeEventListener("toggleSidebar", handleExternalToggle);
  }, []);

  const handleLogout = async () => {
    Cookies.remove("userToken");
    await dispatch(logoutAsync());
    navigate("/");
    window.location.reload();
    setIsDrawerOpen(false);
  };

  useEffect(() => {
    dispatch(fetchProfileAsync());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) {
        setIsNotificationOpen(false);
      }
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        !userButtonRef.current?.contains(event.target)
      ) {
        setIsDrawerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLinkClick = () => setIsDrawerOpen(false);

  // Show sidebar toggle only on employer dashboard routes
  const showSidebarToggle = location.pathname.includes("/employer-dashboard");

  return (
    <div className="">
      <RoleBasedProtectedRoute
        onRoleCheck={(role) => {
          // noop
        }}
      >
        {({ role }) => {
          if (role === "admin" && !location.pathname.startsWith("/admin-dashboard")) {
            return <Navigate to="/admin-dashboard" replace />;
          }

          if (role === "admin" && location.pathname.startsWith("/admin-dashboard")) {
            return <Outlet />;
          }

          if (role === "candidate" && !location.pathname.startsWith("/candidate-dashboard")) {
            window.location.href = "https://indiahirings.com/";
            return null;
          }

          return (
            <>
              {/* Fixed height header to compute mobile sidebar top offset */}
              <header className={`sticky top-0 z-50 ${scrolled ? "bg-white shadow-sm" : "bg-white"} h-14 md:h-16`}>
                <nav className="h-full mx-auto px-3 flex justify-between items-center">
                  <div className="flex items-center">
                    {showSidebarToggle && (
                      <button
                        className="mr-1 px-2 py-1 bg-blue-500 text-white rounded lg:hidden"
                        onClick={toggleSidebar}
                        aria-label="Toggle sidebar"
                      >
                        <FontAwesomeIcon icon={isSidebarOpen ? faTimes : faBars} />
                      </button>
                    )}
                    <Link to="/" className="flex items-center">
                      <img
                        src={d1 || "/placeholder.svg?height=40&width=40"}
                        className="h-12 sm:h-14 w-auto mr-2"
                        alt="Logo"
                      />
                    </Link>
                  </div>

                  <RoleBasedProtectedRoute onRoleCheck={() => { }}>
                    {({ role }) =>
                      role === "candidate" ? (
                        <div className="hidden md:flex space-x-14">
                          <Link
                            to="/"
                            className="text-gray-700 font-medium hover:text-blue-600 transition-colors duration-200"
                          >
                            Home
                          </Link>
                          <Link
                            to="/jobs"
                            className="text-gray-700 font-medium hover:text-blue-600 transition-colors duration-200"
                          >
                            Find Jobs
                          </Link>
                          <Link
                            to="/company-list"
                            className="text-gray-700 font-medium hover:text-blue-600 transition-colors duration-200"
                          >
                            Companies
                          </Link>
                        </div>
                      ) : null
                    }
                  </RoleBasedProtectedRoute>

                  <div className="flex items-center space-x-4 px-2.5 py-1 rounded-3xl border-1">
                    <Link to="/employer-dashboard" className="inline-flex items-center">
                      <Bars3BottomLeftIcon className="h-6 w-6 sm:h-7 sm:w-7" />
                    </Link>

                    <div className="relative" ref={dropdownRef}>
                      <button
                        ref={userButtonRef}
                        onClick={toggleDrawer}
                        className="text-gray-600 hover:text-blue-600 transition-colors duration-200 focus:outline-none relative z-50 flex items-center justify-center"
                        aria-label="Open user menu"
                      >
                        {companyProfile?.company_logo ? (
                          <Image
                            src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${companyProfile?.company_logo}`}
                            className="h-7 w-7 sm:h-8 sm:w-8 object-cover rounded-full border-2 border-gray-200 hover:border-blue-300 transition-colors"
                            alt="Profile"
                          />
                        ) : (
                          <div className="h-7 w-7 sm:h-8 sm:w-8 rounded-full bg-gray-100 border-2 border-gray-200 hover:border-blue-300 flex items-center justify-center transition-colors">
                            <FontAwesomeIcon icon={faUser} className="text-lg text-gray-600" />
                          </div>
                        )}
                      </button>
                    </div>
                  </div>
                </nav>
              </header>

              {/* Overlay for user dropdown */}
              {isDrawerOpen && (
                <div
                  className="fixed inset-0 bg-black bg-opacity-10 z-40 transition-opacity duration-300 ease-in-out"
                  onClick={() => setIsDrawerOpen(false)}
                />
              )}

              {/* User Dropdown */}
              {userData && isDrawerOpen && (
                <div
                  className="user-dropdown fixed right-4 top-12 mt-2 w-72 md:w-96 bg-white rounded-lg shadow-2xl z-50 transform transition-all duration-300 ease-in-out max-h-screen overflow-y-auto"
                  ref={dropdownRef}
                >
                  <div className="relative">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-700 text-white p-3 rounded-t-lg">
                      <div className="flex items-start space-y-1">
                        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center mr-3 shadow-md">
                          {companyProfile?.company_logo ? (
                            <Image
                              src={`${EMPLOYER_COMPANY_LOGO_SAVE_URL}${companyProfile?.company_logo}`}
                              className="h-16 w-16 object-contain rounded-full"
                            />
                          ) : (
                            <span className="text-2xl font-bold text-blue-600">{userData.name.charAt(0)}</span>
                          )}
                        </div>
                        <div className="space-y-1">
                          <div className="text-xl font-semibold">{userData.name}</div>
                          <p className="text-sm text-blue-100">{userData.email}</p>
                        </div>
                      </div>
                    </div>

                    <div className="p-3">
                      <h2 className="text-lg font-semibold mb-2 text-gray-700 border-b pb-2">Account Options</h2>
                      <RoleBasedProtectedRoute onRoleCheck={() => { }}>
                        {({ role }) => (
                          <div className="space-y-0">
                            <Link
                              to={role === "candidate" ? "/candidate-dashboard" : "/employer-dashboard"}
                              className="flex items-center justify-between px-2 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors duration-200"
                              onClick={handleLinkClick}
                            >
                              <div className="flex items-center">
                                <span className="mr-3 w-5 text-blue-500">
                                  <FontAwesomeIcon icon={faHome} />
                                </span>
                                <span>My Profile</span>
                              </div>
                              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
                            </Link>
                            <Link
                              to={"/employer-dashboard/change-password"}
                              className="flex items-center justify-between px-2 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-md transition-colors duration-200"
                              onClick={handleLinkClick}
                            >
                              <div className="flex items-center">
                                <span className="mr-3 w-5 text-blue-500">
                                  <FontAwesomeIcon icon={faClipboardList} />
                                </span>
                                <span>Change Password</span>
                              </div>
                              <FontAwesomeIcon icon={faChevronRight} className="text-gray-400" />
                            </Link>
                          </div>
                        )}
                      </RoleBasedProtectedRoute>

                      <div className="mt-6 border-t pt-3">
                        <button
                          onClick={handleLogout}
                          className="w-full py-2 bg-red-600 text-white font-medium rounded-md hover:bg-red-700 transition-colors duration-200 flex items-center justify-center shadow-md"
                        >
                          <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
                          Logout
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </>
          );
        }}
      </RoleBasedProtectedRoute>

      <div>
        <RoleBasedProtectedRoute onRoleCheck={() => { }}>
          {({ role }) => {
            if (role === "employer" && !location.pathname.startsWith("/employer-dashboard")) {
              return <Navigate to="/employer-dashboard" replace />;
            }
            return null;
          }}
        </RoleBasedProtectedRoute>
      </div>
    </div>
  );
}